import { useParams, useLocation, useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { useGetMemberTask } from "services/tasks";
import { useMemberDetails } from "components/context/MemberDetailsContext";
import Loading from "components/common/Loading";
import { Box } from "@mui/system";
import useIsMobile from "components/hooks/useIsMobile";
import TaskDetailsPanel from "components/tasks/TaskDetailsPanel";
import TaskDropDown from "components/tasks/TaskDropDown";
import Breadcrumbs from "components/common/Breadcrumbs";
import { Task } from "protogen/tasks_pb";
import { DelegationTask } from "protogen/delegations_pb";
import NotFound from "components/NotFound";
import TaskProjectLabels from "components/tasks/TaskProjectLabels";

export default ({ addTask = false }: { addTask?: boolean }) => {
  let params = useParams();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const isLightningTask = searchParams.get("lightning") === "true";
  const inspiredTask = location.state?.inspiredTask;
  const taskWishlistEntry = location.state?.taskWishlistEntry;
  const initialTaskTitle = location.state?.taskTitle;
  const calendarEventRef = location.state?.calendarEventRef;
  const calendarEventSourceType = location.state?.calendarEventSourceType;
  const [task, setTask] = useState<Task | undefined>(undefined);
  const [delegationTasks, setDelegationTasks] = useState<DelegationTask[]>([]);
  const [isUnsavedTask, setIsUnsavedTask] = useState(false);
  const family = location.state?.family;
  const { loading, request, error } = useGetMemberTask((r) => {
    setTask(r.task);
    setDelegationTasks(r.delegationTasks);
  });
  const { data: memberDetails } = useMemberDetails();
  const [refreshKey, setRefreshKey] = useState(0);
  const isMobile = useIsMobile();
  useEffect(() => {
    // if the task ref changes to a different task, inspired task, new task, reset child state
    if (addTask) {
      setRefreshKey(refreshKey + 1);
    }
    if (addTask) {
      const newTask = new Task({ state: 1, priority: "medium", title: "" });
      if (inspiredTask) {
        newTask.title = inspiredTask.title;
        newTask.sourceEntityType = "task_inspiration";
        newTask.sourceEntityRef = inspiredTask.ref;
      } else if (taskWishlistEntry) {
        newTask.title = taskWishlistEntry.title;
        newTask.sourceEntityType = "task_wishlist";
        newTask.sourceEntityRef = taskWishlistEntry.ref;
      } else if (calendarEventRef) {
        newTask.title = initialTaskTitle || "";
        // Use different source type for external vs internal events
        newTask.sourceEntityType =
          calendarEventSourceType === "external"
            ? "external_event"
            : "calendar_event";
        newTask.sourceEntityRef = calendarEventRef;
      } else if (initialTaskTitle) {
        newTask.title = initialTaskTitle;
      }
      setTask(newTask);
      setIsUnsavedTask(true);
      return;
    }
    if (isUnsavedTask) return;
    request({ taskRef: params.taskRef! });
  }, [
    params.taskRef,
    calendarEventRef,
    calendarEventSourceType,
    initialTaskTitle,
  ]);

  if (error) return <NotFound title={"Task not found"} />;
  if (!task || (!task && loading)) return <Loading />;

  return (
    <Box
      sx={{
        margin: isMobile ? "" : "0 auto",
        maxWidth: "850px",
        padding: isMobile ? "20px" : "64px 0",
        gap: "8px",
      }}
    >
      <Box display="flex" flexDirection="row" maxWidth="850px">
        <Box display="flex" flexDirection="column" width="100%">
          <Box
            sx={{
              display: "flex",
              width: "100%",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Breadcrumbs
              breadcrumbs={[
                {
                  name: "Home",
                  link: "/home",
                },
                { name: "Tasks", back: true },
              ]}
            />
            {!isUnsavedTask && (
              <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <TaskProjectLabels
                  isMember={true}
                  initialTask={task}
                  editable={true}
                  onUpdate={(updatedTask) => {
                    setTask(updatedTask);
                  }}
                />
                <TaskDropDown
                  task={task}
                  delegationTasks={delegationTasks}
                  refresh={() => request({ taskRef: task?.ref! })}
                />
              </Box>
            )}
          </Box>
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          marginTop: "8px",
        }}
      >
        <TaskDetailsPanel
          key={refreshKey}
          task={task}
          updateTask={(t) => {
            setTask(t);
          }}
          accountType="member"
          isUnsavedTask={isUnsavedTask}
          initialIsLightningTask={isLightningTask}
          family={family || memberDetails?.family}
          refreshTask={async () => {
            const resp = await request({
              taskRef: params.taskRef!,
            });
            if (resp?.task) {
              setTask(resp.task);
            }
            if (resp?.delegationTasks) {
              setDelegationTasks(resp.delegationTasks);
            }
          }}
          delegationTasks={delegationTasks}
        />
      </Box>
    </Box>
  );
};
